import React from "react";
import { FiDownload, FiMail, FiPhone } from "react-icons/fi";
import {
  contact,
  education,
  experience,
  profile,
  projects,
  skillGroups,
  socials,
} from "../data/profile";

/**
 * The resume is rendered as real HTML rather than an embedded PDF so it is
 * readable on mobile, indexable by search engines, and always in sync with
 * `data/profile.js`.
 *
 * The download action serves the original PDF from the public directory.
 */
function Resume() {
  const availableSocials = socials.filter((social) => Boolean(social.url));

  return (
    <div className="resume">
      <div className="container">
        <div className="resume__toolbar no-print">
          <div>
            <h1 className="section__title">Resume</h1>
            <p className="section__subtitle">
              A one-page summary. Use the button to save it as a PDF.
            </p>
          </div>
          <a
            className="btn btn--primary"
            href="/r4.pdf"
            download="Nikhil_Raj_Resume.pdf"
          >
            <FiDownload aria-hidden="true" />
            Download PDF
          </a>
        </div>

        <article className="resume__doc">
          <header className="resume__header">
            <h2 className="resume__name">{profile.name}</h2>
            <p className="resume__contact-line">
              <span>
                <FiPhone aria-hidden="true" /> {contact.phone}
              </span>
              <a href={`mailto:${contact.email}`}>
                <FiMail aria-hidden="true" /> {contact.email}
              </a>
              {availableSocials.map((social) => (
                <a
                  key={social.id}
                  href={social.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {social.label}
                </a>
              ))}
            </p>
          </header>

          <section>
            <h3 className="resume__section-title">Summary</h3>
            <p>{profile.summary}</p>
          </section>

          <section>
            <h3 className="resume__section-title">Education</h3>
            {education.map((item) => (
              <div className="resume__block" key={item.institution}>
                <div className="timeline__header">
                  <h4>{item.institution}</h4>
                  <span className="timeline__period">{item.period}</span>
                </div>
                <div className="timeline__header">
                  <span>{item.qualification}</span>
                  <strong className="timeline__period">{item.result}</strong>
                </div>
              </div>
            ))}
          </section>

          <section>
            <h3 className="resume__section-title">Experience</h3>
            {experience.map((role) => (
              <div
                className="resume__block"
                key={`${role.organisation}-${role.role}`}
              >
                <div className="timeline__header">
                  <h4>
                    {role.role}{" "}
                    <span className="timeline__org">· {role.organisation}</span>
                  </h4>
                  <span className="timeline__period">{role.period}</span>
                </div>
                <ul className="bullets">
                  {role.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <section>
            <h3 className="resume__section-title">Projects</h3>
            {projects.map((project) => (
              <div className="resume__block" key={project.id}>
                <div className="timeline__header">
                  <h4>
                    {project.title}
                    {project.subtitle && (
                      <span className="timeline__org"> · {project.subtitle}</span>
                    )}
                  </h4>
                  {(project.repo || project.demo) && (
                    <span className="timeline__period">
                      {project.repo && (
                        <a
                          href={project.repo}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          GitHub
                        </a>
                      )}
                      {project.repo && project.demo && " · "}
                      {project.demo && (
                        <a
                          href={project.demo}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Live Demo
                        </a>
                      )}
                    </span>
                  )}
                </div>
                <ul className="bullets">
                  {project.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                  <li>
                    <strong>Technologies:</strong> {project.tech.join(", ")}
                  </li>
                </ul>
              </div>
            ))}
          </section>

          <section>
            <h3 className="resume__section-title">Technical Skills</h3>
            {skillGroups.map((group) => (
              <div className="resume__skill-row" key={group.id}>
                <span className="resume__skill-label">{group.title}</span>
                <span className="resume__skill-values">
                  {group.skills.join(", ")}
                </span>
              </div>
            ))}
          </section>
        </article>
      </div>
    </div>
  );
}

export default Resume;
